/**
 * Size chart editing (MERCH-88). Pure: the editor's draft and its moves. The server
 * (lib/catalog/sizeCharts prepareChart) is the judge and answers 422 with every problem;
 * `problems` here mirrors its limits so the editor can say so before a round trip.
 *
 * A draft: { brand_id, product_type, name, note, columns: [label…], rows: [[cell…]…] }.
 * The first column names the size (unique per chart).
 */

export const LIMITS = Object.freeze({ columns: 12, rows: 60, cell: 40, name: 120, note: 300 });

const parse = (v, d) => {
  if (v == null || v === '') return d;
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch { return d; }
};

/** A new chart: Size and one measurement, three sizes to fill in. */
export function blankChart(productType = '') {
  return { brand_id: '', product_type: productType, name: '', note: '', columns: ['Size', 'Chest (cm)'], rows: [['S', ''], ['M', ''], ['L', '']] };
}

/** A stored row (columns_json / rows_json) as a draft. */
export function chartDraft(row) {
  return {
    id: row.id, brand_id: row.brand_id == null ? '' : String(row.brand_id), product_type: row.product_type || '',
    name: row.name || '', note: row.note || '', columns: parse(row.columns_json, []), rows: parse(row.rows_json, []),
  };
}

export const addColumn = (d) => (d.columns.length >= LIMITS.columns ? d : { ...d, columns: [...d.columns, ''], rows: d.rows.map((r) => [...r, '']) });
export const removeColumn = (d, i) => (i === 0 || d.columns.length <= 1 ? d : { ...d, columns: d.columns.filter((_, j) => j !== i), rows: d.rows.map((r) => r.filter((_, j) => j !== i)) });
export const renameColumn = (d, i, label) => ({ ...d, columns: d.columns.map((c, j) => (j === i ? label : c)) });
export const addRow = (d) => (d.rows.length >= LIMITS.rows ? d : { ...d, rows: [...d.rows, d.columns.map(() => '')] });
export const removeRow = (d, i) => (d.rows.length <= 1 ? d : { ...d, rows: d.rows.filter((_, j) => j !== i) });
export const setCell = (d, i, j, value) => ({ ...d, rows: d.rows.map((r, n) => (n === i ? r.map((c, m) => (m === j ? value : c)) : r)) });

const clean = (v) => String(v == null ? '' : v).trim().replace(/\s+/g, ' ');

/** What the editor shows before saving: [{ path, message }] (the server checks again). */
export function problems(d) {
  const out = [];
  if (!d.id && !d.brand_id) out.push({ path: 'brand_id', message: 'Pick a brand.' });
  if (!d.id && !d.product_type) out.push({ path: 'product_type', message: 'Pick a product type.' });
  const seen = new Set();
  d.columns.forEach((c, i) => {
    const label = clean(c).toLowerCase();
    if (!label || label.length > LIMITS.cell) out.push({ path: `columns.${i}`, message: `Column names are 1 to ${LIMITS.cell} characters.` });
    else if (seen.has(label)) out.push({ path: `columns.${i}`, message: 'Column names are unique.' });
    seen.add(label);
  });
  const sizes = new Set();
  d.rows.forEach((r, i) => {
    const size = clean(r[0]).toLowerCase();
    if (!size) out.push({ path: `rows.${i}.0`, message: `Row ${i + 1} needs its size.` });
    else if (sizes.has(size)) out.push({ path: `rows.${i}.0`, message: `Size "${clean(r[0])}" is listed twice.` });
    sizes.add(size);
    r.forEach((c, j) => { if (clean(c).length > LIMITS.cell) out.push({ path: `rows.${i}.${j}`, message: `Cells are at most ${LIMITS.cell} characters.` }); });
  });
  return out;
}

/** The request body: create sends brand and type; an update never does (they are fixed). */
export function chartPayload(d) {
  const body = { name: clean(d.name), note: clean(d.note), columns: d.columns.map(clean), rows: d.rows.map((r) => r.map(clean)) };
  return d.id ? body : { ...body, brand_id: Number(d.brand_id), product_type: d.product_type };
}

/**
 * The types a chart can be for: the store's enabled types whose registry entry is sold by size
 * (`size_chart`, from the industry). `registry`: useProductTypes().byKey; `storeTypes`: storeProductTypes rows.
 */
export function sizedStoreTypes(registry, storeTypes) {
  return (storeTypes || []).filter((t) => t.enabled && registry[t.key] && registry[t.key].size_chart).map((t) => registry[t.key]);
}

/** "clothing, footwear or fashion accessories": every registry type sold by size (`types`: useProductTypes().types). */
export function sizedTypesText(types) {
  const names = (types || []).filter((t) => t.size_chart).map((t) => String(t.plural || t.label || t.key).toLowerCase());
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0] || '';
}
